import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Toast from '../../../components/ui/Toast';
import { SupabaseError } from '../../../utils/errorHandling';
import { usePresence } from '../../../hooks/usePresence';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useMessaging } from '../../../contexts/MessagingContext';
import { supabase } from '../../../utils/supabase';

const ProfileHeroSidebar = ({ profile, onEditProfile, onShareProfile, completionPercentage = 0, isPublicView = false, aiAssessments: passedAiAssessments }) => {
  // Handle loading state when profile is null or undefined
  const isLoading = !profile;
  const [showCompletionDetails, setShowCompletionDetails] = useState(false);
  const [aiAssessments, setAiAssessments] = useState(new Map());
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isHiredByViewer, setIsHiredByViewer] = useState(false);
  const navigate = useNavigate();
  const { getUserPresence, fetchUserPresence } = usePresence();
  const { userProfile: viewerProfile } = useAuth();
  const { getCurrentPlan } = useSubscription();
  const { createOrGetConversation } = useMessaging();

  // Only employers with a paid subscription tier can see the online status
  const canViewStatus = (() => {
    if (!viewerProfile) return false;
    if (viewerProfile.role === 'admin') return true;
    if (viewerProfile.role !== 'employer') return false;
    const plan = getCurrentPlan();
    return plan?.tier && plan.tier !== 'free';
  })();

  // Employers viewing a public VA profile can see the Contact button
  const canContact = isPublicView && viewerProfile?.role === 'employer';

  // Only paid-tier employers (pro / premium / agency) can actually send messages,
  // OR employers who have already hired this VA (accepted application or active contract)
  const canContactWithPlan = (() => {
    if (!canContact) return false;
    if (isHiredByViewer) return true;
    const plan = getCurrentPlan();
    return plan?.tier && plan.tier !== 'free';
  })();
  
  // Check if the viewing employer has hired this VA (accepted job application or active contract)
  React.useEffect(() => {
    const checkHireStatus = async () => {
      if (!canContact || !profile?.id || !viewerProfile?.id) {
        setIsHiredByViewer(false);
        return;
      }
      try {
        // Check for an accepted job application on one of the employer's jobs
        const { data: jobs } = await supabase
          .from('jobs')
          .select('id')
          .eq('employer_id', viewerProfile.id);

        if (jobs && jobs.length > 0) {
          const jobIds = jobs.map(j => j.id);
          const { data: applications } = await supabase
            .from('job_applications')
            .select('id')
            .eq('applicant_id', profile.id)
            .eq('status', 'accepted')
            .in('job_id', jobIds)
            .limit(1);

          if (applications && applications.length > 0) {
            setIsHiredByViewer(true);
            return;
          }
        }

        // Check for an active contract between employer and VA
        const { data: contracts } = await supabase
          .from('contracts')
          .select('id')
          .eq('va_id', profile.id)
          .eq('employer_id', viewerProfile.id)
          .limit(1);

        setIsHiredByViewer(!!(contracts && contracts.length > 0));
      } catch (err) {
        console.error('Error checking hire status:', err);
        setIsHiredByViewer(false);
      }
    };

    checkHireStatus();
  }, [canContact, profile?.id, viewerProfile?.id]);

  // Fetch presence data for this user on mount/profile change
  React.useEffect(() => {
    if (profile?.id) {
      fetchUserPresence(profile.id);
    }
  }, [profile?.id, fetchUserPresence]);

  // Get real-time presence data for this user (reads from populated context state)
  const presenceData = profile?.id ? getUserPresence(profile.id) : null;

  // Fetch AI skill assessments from database
  React.useEffect(() => {
    if (passedAiAssessments) {
      setAiAssessments(passedAiAssessments);
      return;
    }

    const fetchAiAssessments = async () => {
      if (!profile?.id || isPublicView) return;

      try {
        const { data, error } = await supabase
          .from('ai_skill_assessment_sessions')
          .select('skill_name, final_score, proficiency_level')
          .eq('user_id', profile.id)
          .eq('passed', true)
          .order('updated_at', { ascending: false });

        if (error) throw error;

        const assessmentMap = new Map();
        if (data && data.length > 0) {
          data.forEach(assessment => {
            if (!assessmentMap.has(assessment.skill_name)) {
              assessmentMap.set(assessment.skill_name, assessment);
            }
          });
        }
        setAiAssessments(assessmentMap);
      } catch (error) {
        console.error('Error fetching AI assessments in ProfileHero:', error);
      }
    };

    fetchAiAssessments();
  }, [profile?.id, isPublicView, passedAiAssessments]);

  // Currency symbol helper
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'AUD': 'A$',
      'CAD': 'C$', 'CHF': 'CHF', 'CNY': '¥', 'INR': '₹', 'PHP': '₱'
    };
    return symbols[currency] || currency || '$';
  };

  const handleShareProfile = () => {
    if (!profile) return;
    
    const publicProfileUrl = profile.public_username 
      ? `${window.location.origin}/va/${profile.public_username}`
      : window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: `${profile?.full_name || 'Virtual Assistant'} - Virtual Assistant`,
        text: profile.bio || profile.headline || 'Professional Virtual Assistant',
        url: publicProfileUrl,
      });
    } else {
      navigator.clipboard.writeText(publicProfileUrl);
      onShareProfile?.();
    }
  };

  const handleEditProfile = () => {
    navigate('/va-profile-settings');
  };

  const handleContactVA = async () => {
    if (!profile?.id || !canContactWithPlan) return;
    setIsSendingMessage(true);
    try {
      const result = await createOrGetConversation(profile.id);
      const conv = result?.data;
      const conversationId = conv?.conversation_id || conv?.id;
      if (!conversationId) {
        console.error('Failed to get conversation ID from result:', result);
        setToast({ show: true, message: 'Could not open a conversation with this VA. Please try again.', type: 'error' });
        navigate('/messages');
        return;
      }
      navigate('/messages', {
        state: {
          preSelectedConversation: {
            id: conversationId,
            conversation_id: conversationId,
            recipientId: profile.id,
            recipientName: profile.full_name || 'Virtual Assistant',
            recipientRole: 'va',
            recipientAvatar: profile.profile_picture || null,
          }
        }
      });
    } catch (err) {
      console.error('Failed to start conversation:', err);
      const friendlyMessage = err instanceof SupabaseError
        ? err.getUserFriendlyMessage()
        : new SupabaseError(err?.message || 'Failed to start conversation', err).getUserFriendlyMessage();
      setToast({ show: true, message: friendlyMessage, type: 'error' });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const getOnlineStatusIndicator = () => {
    const status = presenceData?.status || profile?.availability_status;
    if (!status || typeof status !== 'string') return 'bg-gray-500';
    
    if (status === 'online') return 'bg-green-500';
    if (status === 'away' || status === 'idle') return 'bg-yellow-500';
    if (status === 'offline') return 'bg-gray-500';
    
    switch(status.toLowerCase()) {
      case 'available': return 'bg-green-500';
      case 'partially_available':
      case 'busy': return 'bg-yellow-500';
      case 'unavailable': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getCompletionColor = () => {
    if (completionPercentage >= 80) return 'text-green-600';
    if (completionPercentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const countVerifiedSkills = () => {
    if (!profile?.skills || !Array.isArray(profile.skills)) return 0;
    
    const verifiedCount = new Set();
    
    profile.skills.forEach(skill => {
      const skillName = typeof skill === 'string' ? skill : skill?.name;
      if (skillName) {
        if (skill.verified || (skill.ai_assessed && skill.ai_assessment_score >= 70)) {
          verifiedCount.add(skillName);
        }
      }
    });
    
    aiAssessments.forEach((assessment, skillName) => {
      if (assessment.final_score && Number(assessment.final_score) >= 70) {
        verifiedCount.add(skillName);
      }
    });
    
    return verifiedCount.size;
  };

  const getMissingProfileItems = () => {
    if (!profile) return [];
    
    const missingItems = [];
    
    if (!profile.full_name || profile.full_name.trim() === '') 
      missingItems.push('Add your full name');
    if (!profile.title || profile.title.trim() === '') 
      missingItems.push('Add a professional title');
    if (!profile.bio || profile.bio.length < 30) 
      missingItems.push('Add a professional bio');
    if (!profile.skills || profile.skills.length < 3) 
      missingItems.push(`Add more skills`);
    if (!profile.profile_picture || profile.profile_picture.trim() === '') 
      missingItems.push('Upload profile picture');
    if (!profile.portfolio_items || profile.portfolio_items.length === 0) 
      missingItems.push('Add portfolio items');
    
    return missingItems.slice(0, 3);
  };

  return (
    <>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}
      <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-4">
        {/* Avatar Section */}
        <div className="mb-4 flex flex-col items-center">
          <div className="w-48 h-48 rounded-full overflow-hidden border-2 border-white shadow-lg">
            {isLoading ? (
              <div className="w-full h-full bg-gray-200 animate-pulse" />
            ) : (
              <Image
                src={profile?.profile_picture || '/assets/images/Logo/webp/VAS-LOGO.webp'}
                alt={`${profile?.full_name || 'Virtual Assistant'}'s profile`}
                className="w-full bg-white h-full object-contain"
              />
            )}
          </div>
          {/* Online Status Indicator - visible to paid employer subscribers only */}
          {!isLoading && canViewStatus && (
            <div className="flex items-center gap-1.5 mt-2 bg-white/20 rounded-full px-3 py-1">
              <div className={`w-2.5 h-2.5 ${getOnlineStatusIndicator()} rounded-full flex-shrink-0`}></div>
              <span className="text-xs font-medium">
                {(() => {
                  const status = presenceData?.status || profile?.availability_status;
                  if (!status) return 'Offline';
                  const labels = {
                    online: 'Online', available: 'Available',
                    away: 'Away', idle: 'Away', busy: 'Busy',
                    partially_available: 'Partially Available',
                    offline: 'Offline', unavailable: 'Unavailable',
                  };
                  return labels[status.toLowerCase()] || status;
                })()}
              </span>
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="text-center mb-4">
          {isLoading ? (
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto" />
          ) : (
            <>
              <h1 className="text-xl font-bold mb-1">{profile?.full_name || 'Virtual Assistant'}</h1>
              {profile?.identity_verification_status === 'verified' && (
                <div className="flex justify-center mb-2">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-emerald-600 rounded-full text-xs font-bold shadow-md"
                    title="VA has been verified by VA Sprouts"
                  >
                    <Icon name="ShieldCheck" size={13} className="text-emerald-500" />
                    ID Verified
                  </span>
                </div>
              )}
              <p className="text-primary-100 text-sm mb-2">{profile?.title || 'Professional Virtual Assistant'}</p>
              
              {/* Location & Timezone */}
              <div className="flex flex-col gap-1 text-xs text-primary-100 mb-3">
                {profile?.location && (
                  <div className="flex items-center justify-center">
                    <Icon name="MapPin" size={12} className="mr-1" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile?.timezone && (
                  <div className="flex items-center justify-center">
                    <Icon name="Clock" size={12} className="mr-1" />
                    <span>{profile.timezone}</span>
                  </div>
                )}
              </div>

              {/* Rate */}
              {(profile?.minimum_rate || profile?.maximum_rate || profile?.hourly_rate) && (
                <div className="bg-white/10 rounded-lg px-3 py-2 backdrop-blur-sm mb-3">
                  <div className="flex items-center justify-center">
                    <Icon name="DollarSign" size={14} className="mr-1" />
                    <span className="text-sm font-medium">
                      {profile.minimum_rate && profile.maximum_rate
                        ? `${profile.minimum_rate}-${profile.maximum_rate}/hr`
                        : profile.hourly_rate
                          ? `${profile.hourly_rate}/hr`
                          : 'Rate upon request'
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 mb-4">
                <Button 
                  variant="outline"
                  size="sm"
                  icon={<Icon name="Share2" size={14} />}
                  iconPosition="left"
                  onClick={handleShareProfile}
                  disabled={isLoading}
                  className="flex-1 bg-white text-primary-600 border-white hover:bg-white/90"
                >
                  Share
                </Button>
                {!isPublicView && (
                  <Button 
                    variant="outline"
                    size="sm"
                    icon={<Icon name="Edit" size={14} />}
                    iconPosition="left"
                    onClick={handleEditProfile}
                    className="flex-1 bg-white/20 text-white border-white hover:bg-white/30"
                  >
                    Edit
                  </Button>
                )}
                {canContact && (
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Icon name="MessageSquare" size={14} />}
                    iconPosition="left"
                    onClick={handleContactVA}
                    disabled={!canContactWithPlan || isSendingMessage}
                    title={!canContactWithPlan ? 'Upgrade to Pro, Premium, or Agency to message VAs' : (isHiredByViewer ? `Message ${profile?.full_name || 'this VA'} (hired)` : `Message ${profile?.full_name || 'this VA'}`)}
                    className={`flex-1 border-white ${
                      canContactWithPlan
                        ? 'bg-white text-primary-600 hover:bg-white/90'
                        : 'bg-white/20 text-white/50 cursor-not-allowed'
                    }`}
                  >
                    {isSendingMessage ? 'Opening...' : 'Message'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Profile Completion */}
        {!isPublicView && completionPercentage < 100 && (
          <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium">Profile Completion</span>
              <span className={`text-xs font-bold ${getCompletionColor()}`}>{completionPercentage}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full ${
                  completionPercentage >= 80 ? 'bg-green-400' : 
                  completionPercentage >= 50 ? 'bg-yellow-400' : 
                  'bg-red-400'
                }`}
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
            
            {completionPercentage < 100 && (
              <div className="mt-2 text-xs text-white/80">
                <ul className="space-y-1">
                  {getMissingProfileItems().map((item, index) => (
                    <li key={index} className="flex items-start">
                      <Icon name="AlertCircle" size={12} className="mr-1 mt-0.5 flex-shrink-0 text-yellow-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Metrics - Compact Grid */}
        <div>
          <h3 className="text-xs font-semibold text-white/90 mb-2">Performance Metrics</h3>
          
          {isLoading ? (
            <div className="grid grid-cols-2 gap-1.5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/10 rounded-lg p-1.5 backdrop-blur-sm animate-pulse">
                  <div className="h-4 w-16 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {/* Rating */}
              {profile?.rating !== null && profile?.rating !== undefined && (
                <div className="bg-white/10 rounded-lg p-1.5 backdrop-blur-sm col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon name="Star" size={12} className="text-yellow-300 mr-1.5" />
                      <div>
                        <div className="text-xs font-bold">{profile.rating || 0} / 5</div>
                        <div className="text-[10px] text-primary-100">{profile?.reviewCount > 0 ? `${profile.reviewCount} reviews` : 'No reviews yet'}</div>
                      </div>
                    </div>
                    {profile?.rating && parseFloat(profile.rating) >= 4.5 && (
                      <span className="bg-yellow-500 text-white text-[10px] px-1 py-0.5 rounded-full">
                        Top
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Verified Skills */}
              <div className="bg-white/10 rounded-lg p-1.5 backdrop-blur-sm">
                <div className="flex items-center">
                  <Icon name="Award" size={12} className="text-blue-300 mr-1.5" />
                  <div>
                    <div className="text-xs font-bold">{countVerifiedSkills()}</div>
                    <div className="text-[10px] text-primary-100">Verified</div>
                  </div>
                </div>
              </div>

              {/* Portfolio Items */}
              <div className="bg-white/10 rounded-lg p-1.5 backdrop-blur-sm">
                <div className="flex items-center">
                  <Icon name="Briefcase" size={12} className="text-green-300 mr-1.5" />
                  <div>
                    <div className="text-xs font-bold">{profile?.portfolio_items?.length || 0}</div>
                    <div className="text-[10px] text-primary-100">Portfolio</div>
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div className="bg-white/10 rounded-lg p-1.5 backdrop-blur-sm">
                <div className="flex items-center">
                  <Icon name="Briefcase" size={12} className="text-orange-300 mr-1.5" />
                  <div>
                    <div className="text-xs font-bold">{profile?.workExperienceCount || 0}</div>
                    <div className="text-[10px] text-primary-100">Experience</div>
                  </div>
                </div>
              </div>

              {/* Clients */}
              <div className="bg-white/10 rounded-lg p-1.5 backdrop-blur-sm">
                <div className="flex items-center">
                  <Icon name="Users" size={12} className="text-purple-300 mr-1.5" />
                  <div>
                    <div className="text-xs font-bold">{profile?.clientCount || 0}</div>
                    <div className="text-[10px] text-primary-100">Clients</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Links */}
        {!isLoading && (
          <div className="mt-4 space-y-2">
            {!isPublicView && (
              <Button 
                className="w-full bg-white text-primary-600 hover:bg-white/90"
                size="sm"
                icon={<Icon name="Briefcase" size={14} />}
                iconPosition="left"
                onClick={() => navigate('/va-portfolio-management')}
              >
                Manage Portfolio
              </Button>
            )}
            
            {/* External Links */}
            <div className="flex flex-wrap gap-2 justify-center">
              {profile?.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline"
                    size="sm" 
                    icon={<Icon name="Globe" size={12} />}
                    iconPosition="left"
                    className="bg-white/20 text-white border-white hover:bg-white/30"
                  >
                    Website
                  </Button>
                </a>
              )}
              {profile?.linkedin && (
                <a href={profile.linkedin} target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline"
                    size="sm" 
                    icon={<Icon name="Linkedin" size={12} />}
                    iconPosition="left"
                    className="bg-white/20 text-white border-white hover:bg-white/30"
                  >
                    LinkedIn
                  </Button>
                </a>
              )}
              {profile?.github_url && (
                <a href={profile.github_url} target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline"
                    size="sm" 
                    icon={<Icon name="GitHub" size={12} />}
                    iconPosition="left"
                    className="bg-white/20 text-white border-white hover:bg-white/30"
                  >
                    GitHub
                  </Button>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProfileHeroSidebar;
